import { useState, type ReactNode } from 'react';
import {
  CalendarCheck,
  CalendarRange,
  FileText,
  Megaphone,
  RotateCcw,
  School,
  Users,
  Video,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { DatePicker } from '@/components/ui/DatePicker';
import { EmptyState } from '@/components/ui/EmptyState';
import { MetricTile, type MetricTone } from '@/components/ui/MetricTile';
import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useApiQuery } from '@/hooks/useApiQuery';
import { ApiRequestError } from '@/lib/apiClient';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { parentApi } from '../api/parent.api';
import { ChildAttendance } from '../components/dashboard/ChildAttendance';
import { ChildrenList } from '../components/dashboard/ChildrenList';
import { NoticeList } from '../components/dashboard/NoticeList';
import { TodaySchedule, type DayState } from '../components/dashboard/TodaySchedule';
import { useParentChild, useSelectedChild } from '../store/child.store';
import { PARENT_PALETTE } from '../theme';
import type { ParentTimeframe } from '../types/overview';
import { todayIso } from '../utils';

/**
 * The parent panel's first screen: one child's day, on one date.
 *
 * Reads `GET /parent/children/:uuid/overview` — one request for every block, so
 * the tiles, the schedule and the register cannot drift apart. Each figure is
 * read on the server through the same reader the page behind its tile uses, so
 * "71.4%" here is "71.4%" on the Attendance page.
 *
 * What the mock this replaced drew and nothing holds is gone: "Allocated
 * Classes", "Total Students" and "Periods / Week" (a teacher's dashboard, copied
 * across, with the weekly figure multiplied up from the day's), a "Message
 * teacher" button onto an inbox that cannot send, and a rotating tip whose
 * quotes were signed "Your school".
 *
 * Which child is being read is the rail's decision, not this screen's: the
 * switcher in the sidebar writes to `child.store`, and everything here follows
 * it. The child is never in the address — an id in the URL is an id somebody
 * can change (CLAUDE.md §10).
 */
export function ParentDashboardPage() {
  const user = useAuthStore((state) => state.user);
  const child = useSelectedChild();
  const isLoadingChildren = useParentChild((state) => state.isLoading);
  const childrenError = useParentChild((state) => state.error);
  const reloadChildren = useParentChild((state) => state.load);

  /**
   * The school's day in India, for the first request only. Once the overview
   * lands, its own `today` is used instead — the server's clock, not a device
   * that may be set wrong.
   */
  const firstToday = todayIso();
  /** Empty once the filter is cleared, which reads as "back to today". */
  const [pickedDate, setPickedDate] = useState<string>(firstToday);
  const [timeframe, setTimeframe] = useState<ParentTimeframe>('this_week');

  const requestedDate = pickedDate || firstToday;

  const { data, isLoading, error, refetch } = useApiQuery(
    () => (child ? parentApi.overview(child.uuid, requestedDate) : Promise.resolve(null)),
    [child?.uuid, requestedDate],
  );

  // Switching children refetches; until the new day lands, the old one belongs
  // to somebody else and must not be drawn under this child's name.
  const overview = data && data.child.uuid === child?.uuid ? data : null;

  if (isLoadingChildren) {
    return (
      <div className={cn(PARENT_PALETTE, 'flex flex-col gap-6')}>
        <Skeleton className="h-8 w-64" />
        <TileSkeletons />
        <Skeleton className="h-96 rounded-3xl" />
      </div>
    );
  }

  if (childrenError) {
    return (
      <div className={cn(PARENT_PALETTE)}>
        <QueryError
          title="Could not load your children"
          error={new ApiRequestError(childrenError, 0, 'INTERNAL_ERROR')}
          onRetry={() => void reloadChildren()}
        />
      </div>
    );
  }

  // The school office links a child to a parent's account, so an account with
  // none of them is a real state and not an error — and every card below is
  // drawn from a child, which is why this stands in front of all of them.
  if (!child) {
    return (
      <div className={cn(PARENT_PALETTE, 'card')}>
        <EmptyState
          icon={<Users className="h-5 w-5" />}
          title="No child linked to your account yet"
          description="Your school office links a parent's account to their child's record. Once that is done, your child's day, attendance, homework and notices all appear here."
        />
      </div>
    );
  }

  // The server may answer for a different day than asked — a date outside the
  // session falls back to today — so everything below reads the day it got.
  const date = overview?.date ?? requestedDate;
  const today = overview?.today ?? firstToday;
  const dayState: DayState = date === today ? 'today' : date < today ? 'past' : 'future';
  const dayLabel = dayState === 'today' ? `Today, ${formatDate(date)}` : formatDate(date);

  return (
    <div className={cn(PARENT_PALETTE, 'flex flex-col gap-6')}>
      {/* ---------------- greeting and the day being read ---------------- */}
      <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <span>
              {greetingFor(new Date())}, {user?.firstName ?? 'there'}!
            </span>
            <span aria-hidden>👋</span>
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Here&rsquo;s {child.fullName}&rsquo;s day at school, in {child.className}.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <DatePicker
            label="Show the day"
            hideLabel
            size="sm"
            isClearable
            value={pickedDate}
            onChange={(event) => setPickedDate(event.target.value)}
            className="w-full sm:w-44 border-line-strong border-2 shadow-sm"
          />

          {requestedDate !== today && (
            <button
              type="button"
              onClick={() => setPickedDate(today)}
              className="flex items-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-xs font-semibold text-indigo-600 dark:text-indigo-400 transition-colors hover:border-indigo-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 cursor-pointer"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              Today
            </button>
          )}
        </div>
      </header>

      {error ? (
        <QueryError
          title={`Could not load ${child.fullName}'s day`}
          error={error}
          onRetry={refetch}
        />
      ) : !overview || isLoading ? (
        <>
          <TileSkeletons />
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
            <Skeleton className="h-96 rounded-3xl lg:col-span-8" />
            <Skeleton className="h-96 rounded-3xl lg:col-span-4" />
          </div>
        </>
      ) : (
        <>
          {/* ---------------- the five figures ---------------- */}
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 sm:gap-4">
            <TileLink
              to="/parent/timetable"
              tone="violet"
              icon={<CalendarRange className="h-5 w-5" />}
              label={dayState === 'today' ? 'Periods Today' : 'Periods That Day'}
              value={overview.periods.length}
              hint={overview.closedFor ?? formatDate(date)}
            />
            <TileLink
              to="/parent/profile"
              tone="indigo"
              icon={<Users className="h-5 w-5" />}
              label="My Children"
              value={overview.children.length}
              hint="Linked to your account"
            />
            <TileLink
              to="/parent/attendance"
              tone="emerald"
              icon={<CalendarCheck className="h-5 w-5" />}
              label="Attendance This Month"
              value={
                overview.attendance.this_month.overallPercent === null
                  ? '—'
                  : `${overview.attendance.this_month.overallPercent}%`
              }
              hint={
                overview.attendance.this_month.markedDays > 0
                  ? `${overview.attendance.this_month.presentDays + overview.attendance.this_month.lateDays} of ${overview.attendance.this_month.markedDays} days attended`
                  : 'No register taken yet'
              }
            />
            <TileLink
              to="/parent/homework"
              tone="amber"
              icon={<FileText className="h-5 w-5" />}
              label="Pending Homework"
              value={overview.homework.pending}
              hint={
                overview.homework.overdue > 0
                  ? `${overview.homework.overdue} overdue`
                  : 'Nothing overdue'
              }
            />
            <TileLink
              to="/parent/recorded-classes"
              tone="rose"
              icon={<Video className="h-5 w-5" />}
              label="New Recordings"
              value={overview.recordings.newThisWeek}
              hint={
                overview.recordings.total > 0
                  ? `This week · ${overview.recordings.total} in library`
                  : 'No videos uploaded'
              }
              className="sm:col-span-2 md:col-span-1"
            />
          </div>

          {/* ---------------- the day, and the term behind it ---------------- */}
          <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-12">
            <div className="flex flex-col gap-6 lg:col-span-8">
              <TodaySchedule
                periods={overview.periods}
                dayLabel={dayLabel}
                dayState={dayState}
                childName={child.fullName}
                closedFor={overview.closedFor}
              />

              <ChildAttendance
                attendance={overview.attendance[timeframe]}
                days={overview.lastSevenDays}
                timeframe={timeframe}
                onTimeframeChange={setTimeframe}
                rangeLabel={rangeLabelOf(overview.lastSevenDays)}
                childName={child.fullName}
              />
            </div>

            <div className="flex flex-col gap-6 lg:col-span-4">
              <ChildrenList summaries={overview.children} selectedUuid={child.uuid} />

              <NoticeList
                title="Class Notices"
                icon={<Megaphone className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />}
                notices={overview.classNotices}
                total={overview.classNoticesTotal}
                viewAllTo="/parent/notices"
                viewAllLabel="View all notices for this class"
                emptyTitle="Nothing from the class yet"
                emptyDescription={`Notices ${child.fullName}'s teachers send to parents appear here.`}
              />

              <NoticeList
                title="Institute Notices"
                icon={<School className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />}
                tone="info"
                notices={overview.instituteNotices}
                total={overview.instituteNoticesTotal}
                viewAllTo="/parent/notices"
                viewAllLabel="View all notices from the school office"
                emptyTitle="Nothing from the school office yet"
                emptyDescription="Announcements your school puts up for everyone appear here."
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** One headline figure that opens the page it summarises. */
function TileLink({
  to,
  className,
  ...tile
}: {
  to: string;
  tone: MetricTone;
  icon: ReactNode;
  label: string;
  value: ReactNode;
  hint: ReactNode;
  className?: string;
}) {
  return (
    <Link
      to={to}
      className={cn(
        'block rounded-[16px] transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
        className,
      )}
    >
      <MetricTile {...tile} className="h-full" />
    </Link>
  );
}

function TileSkeletons() {
  return (
    <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 sm:gap-4">
      {Array.from({ length: 5 }, (_, index) => (
        <Skeleton key={index} className="h-20 rounded-[16px]" />
      ))}
    </div>
  );
}

/** "18 Sep 2026 - 24 Sep 2026" — the seven days the strip covers. */
function rangeLabelOf(days: { date: string }[]): string {
  if (days.length === 0) return '—';
  return `${formatDate(days[0].date)} - ${formatDate(days[days.length - 1].date)}`;
}

/** How the screen opens, by the reader's own clock. */
function greetingFor(now: Date): string {
  const hour = now.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';

  return 'Good evening';
}
